/**
 * Canonical Response Adapter
 *
 * The analytics backend now returns a canonical response format:
 *   { kpis[], trend?, breakdowns[], meta }
 *
 * The frontend pages still expect the legacy flat format:
 *   { period, summary, trend[], by_product[], by_brand[], ... }
 *
 * This adapter auto-detects canonical responses and converts them to the
 * legacy shape so all existing pages continue working unchanged.
 */

// ── Detection ────────────────────────────────────────────────────────────────

interface CanonicalKpi {
  id: string
  label: string
  value: number | null
  previousValue?: number | null
  variation?: number | null
  format: string
  trend?: string
}

interface CanonicalTrendPoint {
  date: string
  values: Record<string, number | null>
}

interface CanonicalBreakdownRow {
  dimensionValues: Record<string, string>
  measures: Record<string, number | null>
  rank?: number
}

interface CanonicalBreakdown {
  by: string
  dimensions: string[]
  measures: string[]
  rows: CanonicalBreakdownRow[]
}

interface CanonicalMeta {
  factId: string
  period: { from: string; to: string }
  executionTimeMs: number
  rowCount: number
}

interface CanonicalResponse {
  kpis: CanonicalKpi[]
  trend?: {
    granularity: string
    measures: string[]
    points: CanonicalTrendPoint[]
  }
  breakdowns?: CanonicalBreakdown[]
  meta: CanonicalMeta
  filterOptions?: string[] | Record<string, string[]>
  message?: string
  /** Non-numeric summary fields (nested objects) preserved by the backend. */
  summaryExtras?: Record<string, unknown>
  /** Structured breakdown keys (cohort_table, rfm_segments, ...) preserved verbatim. */
  breakdownExtras?: Record<string, unknown>
}

/**
 * Checks whether a response is in canonical analytics format.
 *
 * Canonical responses have `kpis` (array) and `meta` (object with `factId`).
 * Legacy responses have `summary`/`period` instead.
 */
function isCanonicalResponse(data: unknown): data is CanonicalResponse {
  if (typeof data !== 'object' || data === null) return false
  const obj = data as Record<string, unknown>
  return (
    Array.isArray(obj.kpis) &&
    typeof obj.meta === 'object' &&
    obj.meta !== null &&
    'factId' in (obj.meta as Record<string, unknown>)
  )
}

// ── Conversion ───────────────────────────────────���───────────────────────────

/**
 * Converts a canonical analytics response to the legacy flat format.
 *
 * If the input is NOT canonical, it is returned unchanged.
 */
export function adaptCanonicalResponse(data: unknown): unknown {
  if (!isCanonicalResponse(data)) return data

  const result: Record<string, unknown> = {}

  // meta.period → period
  result.period = {
    type: 'custom',
    start_date: data.meta.period.from,
    end_date: data.meta.period.to,
  }

  // kpis → summary (id → value, id_change → variation * 100)
  const summary: Record<string, unknown> = {}
  for (const kpi of data.kpis) {
    summary[kpi.id] = kpi.value
    if (kpi.variation !== undefined && kpi.variation !== null) {
      // Canonical stores variation as ratio (-0.15 = -15%),
      // legacy stores as percentage (-15)
      summary[`${kpi.id}_change`] = kpi.variation * 100
    }
  }
  // Merge non-numeric summary fields (nested objects: best_month, worst_month, ...).
  if (data.summaryExtras) {
    for (const [key, value] of Object.entries(data.summaryExtras)) {
      summary[key] = value
    }
  }
  result.summary = summary

  // trend.points → flat trend array
  // Emit both `date` (canonical) and `period` (legacy backend column alias) so
  // pages that still read `item.period` (PnL, Seasonality, ...) keep working.
  if (data.trend) {
    result.trend = data.trend.points.map((p) => ({
      date: p.date,
      period: p.date,
      ...p.values,
    }))
  }

  // breakdowns → by_* at root level
  if (data.breakdowns) {
    for (const breakdown of data.breakdowns) {
      const flatRows = breakdown.rows.map((row, idx) => ({
        ...row.dimensionValues,
        ...row.measures,
        rank: row.rank ?? idx + 1,
      }))
      result[`by_${breakdown.by}`] = flatRows
    }
  }

  // filterOptions → filter_options
  if (data.filterOptions) {
    result.filter_options = data.filterOptions
  }

  // breakdownExtras → spread at root (cohort_table, rfm_segments, retention_curve,
  // monthly_heatmap, day_of_week, yoy_comparison, new_vs_returning_trend, ...)
  if (data.breakdownExtras) {
    for (const [key, value] of Object.entries(data.breakdownExtras)) {
      result[key] = value
    }
  }

  // message passthrough
  if (data.message) {
    result.message = data.message
  }

  return result
}
